import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { NOTE_MAX_CHARS } from './useVaultNotes'
import { BusyLabel } from '../../components/Spinner'

interface Props {
  value: string
  /** Absent = read-only (no Vault write permission, or notes unavailable). */
  onSave?: (text: string) => Promise<void>
  mobile?: boolean
  /** The item's Badges (chips + 🏷), shown in the card's header row. */
  badges?: ReactNode
  /** Bumped by the viewer's N shortcut: start editing. */
  editSignal?: number
  /** Open straight into editing (a grid tile's ✎); `onDone` once saved or cancelled. */
  autoEdit?: boolean
  onDone?: () => void
}

/** A Vault item's description in the media viewer: shown in full, edited in place with an explicit Save. */
export function NotesEditor({ value, onSave, mobile = false, badges = null, editSignal = 0, autoEdit = false, onDone }: Props) {
  const { t } = useTranslation('library')
  const [editing, setEditing] = useState(autoEdit && Boolean(onSave))
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const box = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const seen = useRef(editSignal)
  useEffect(() => {
    if (editSignal === seen.current) return
    seen.current = editSignal
    if (onSave) setEditing(true)
  }, [editSignal, onSave])

  useEffect(() => {
    const el = box.current
    if (!editing || !el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [editing])

  // Grow with the text up to the CSS max-height, then scroll.
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft, editing])

  const cancel = () => {
    setDraft(value)
    setFailed(false)
    setEditing(false)
    onDone?.()
  }
  const save = async () => {
    if (!onSave || saving) return
    if (draft.trim() === value.trim()) return cancel()
    setSaving(true)
    setFailed(false)
    try {
      await onSave(draft)
      setEditing(false)
      onDone?.()
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const cls = `lib-notes ${mobile ? 'lib-notes--m' : ''}`
  if (editing) {
    return (
      <div className={cls} onClick={(e) => e.stopPropagation()}>
        <div className="lib-notes-head">
          <span className="lib-notes-label">{t('vault.notes.label')}</span>
          {badges}
          <span className="lib-spacer" />
          <span className="lib-notes-count">{t('vault.notes.count', { n: draft.length, max: NOTE_MAX_CHARS })}</span>
        </div>
        <textarea
          ref={box}
          className="lib-notes-input"
          value={draft}
          maxLength={NOTE_MAX_CHARS}
          placeholder={t('vault.notes.placeholder')}
          aria-label={t('vault.notes.label')}
          disabled={saving}
          rows={3}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              cancel()
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void save()
            }
          }}
        />
        <div className="lib-notes-actions">
          {failed ? (
            <span className="lib-notes-error" role="alert">
              {t('vault.notes.failed')}
            </span>
          ) : (
            !mobile && <span className="lib-notes-hint">{t('vault.notes.hint')}</span>
          )}
          <span className="lib-spacer" />
          <button type="button" className="lib-btn lib-btn--ghost" onClick={cancel} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" onClick={() => void save()} disabled={saving}>
            <BusyLabel busy={saving} label={t('vault.notes.saving')}>{t('vault.notes.save')}</BusyLabel>
          </button>
        </div>
      </div>
    )
  }

  if (!value) {
    if (!onSave && !badges) return null
    return (
      <div className={`${cls} lib-notes--bare`} onClick={(e) => e.stopPropagation()}>
        {onSave && (
          <button type="button" className="lib-notes-add" onClick={() => setEditing(true)}>
            <FeatherIcon name="edit-3" size={14} />
            {t('vault.notes.add')}
          </button>
        )}
        {badges}
      </div>
    )
  }

  return (
    <div className={cls} onClick={(e) => e.stopPropagation()}>
      <div className="lib-notes-head">
        <span className="lib-notes-label">{t('vault.notes.label')}</span>
        {badges}
        <span className="lib-spacer" />
        {onSave && (
          <button type="button" className="lib-btn lib-btn--ghost lib-notes-edit" onClick={() => setEditing(true)}>
            <FeatherIcon name="edit-3" size={13} />
            {t('vault.notes.edit')}
          </button>
        )}
      </div>
      <p className="lib-notes-text">{value}</p>
    </div>
  )
}
